import {BaseApi} from './BaseApi'

// ============================================================================
// TYPES
// ============================================================================

export type LenderStatus = 'Active' | 'Inactive'

export interface Lender {
  id: string
  name: string
  status: LenderStatus
  advanceRate?: number // percentage, e.g. 85 = 85%
  paymentTerms?: string
  contactName?: string
  contactEmail?: string
  remittanceAddress?: string
  uccLanguage?: string
}

export interface CreateLenderRequest {
  name: string
  status: LenderStatus
  advanceRate?: number
  paymentTerms?: string
  contactName?: string
  contactEmail?: string
  remittanceAddress?: string
  uccLanguage?: string
}

export interface UpdateLenderRequest extends CreateLenderRequest {
  id: string
}

export interface ListLendersParams {
  status?: LenderStatus
  search?: string
}

// ============================================================================
// SERVICE
// ============================================================================

class LenderApiService extends BaseApi {
  private readonly baseUrl = '/payroll/lenders'

  /** GET /payroll/lenders?status=&search= */
  async list(params?: ListLendersParams): Promise<Lender[]> {
    const query = new URLSearchParams()
    if (params?.status) query.append('status', params.status)
    if (params?.search) query.append('search', params.search)
    const url = query.toString() ? `${this.baseUrl}?${query.toString()}` : this.baseUrl
    const response = await this.client.get<Lender[]>(url)
    return response.data
  }

  /** GET /payroll/lenders/:id */
  async getById(id: string): Promise<Lender> {
    const response = await this.client.get<Lender>(`${this.baseUrl}/${id}`)
    return response.data
  }

  /** POST /payroll/lenders */
  async create(payload: CreateLenderRequest): Promise<Lender> {
    const response = await this.client.post<Lender>(this.baseUrl, payload)
    return response.data
  }

  /** PUT /payroll/lenders/:id */
  async update(id: string, payload: UpdateLenderRequest): Promise<Lender> {
    const response = await this.client.put<Lender>(`${this.baseUrl}/${id}`, payload)
    return response.data
  }

  /** DELETE /payroll/lenders/:id */
  async remove(id: string): Promise<void> {
    await this.client.delete(`${this.baseUrl}/${id}`)
  }
}

export const lenderApi = new LenderApiService()
export default lenderApi
